<script>
  import { onDestroy, onMount } from "svelte"

  export let page
  export let pdf
  export let scale

  let canvas
  let generation = 0
  let renderTask = null
  let visible = false

  // Render lazily and release when far offscreen: a 100-page document held
  // entirely in canvas backing memory can reach gigabytes at retina scales.
  // The parent sizes the page element immediately so scroll length and
  // placement overlays are correct before paint, and keeps that size while
  // the canvas is empty. Once visible, re-render whenever zoom changes the
  // scale. The release margin is far outside the render margin so a page
  // near the boundary can't thrash between rendering and releasing.
  onMount(() => {
    const renderObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) visible = true
      },
      { rootMargin: "100% 0px" }
    )
    const releaseObserver = new IntersectionObserver(
      (entries) => {
        if (visible && entries.every((entry) => !entry.isIntersecting)) {
          visible = false
          release()
        }
      },
      { rootMargin: "300% 0px" }
    )
    releaseObserver.observe(canvas)
    renderObserver.observe(canvas)
    return () => {
      releaseObserver.disconnect()
      renderObserver.disconnect()
    }
  })

  onDestroy(() => renderTask?.cancel())

  $: if (visible) render(scale)

  // Zeroing the dimensions frees the canvas backing store; the generation
  // check skips that when the page re-entered view mid-await, since the new
  // render sizes the canvas itself.
  async function release() {
    const startedGeneration = ++generation
    if (renderTask) {
      renderTask.cancel()
      await renderTask.promise.catch(() => {})
      renderTask = null
    }
    if (startedGeneration !== generation) return
    canvas.height = 0
    canvas.width = 0
  }

  async function render(atScale) {
    // Rapid zooming can overlap renders; the generation counter lets every
    // superseded call bail before touching the canvas.
    const startedGeneration = ++generation
    if (renderTask) {
      renderTask.cancel()
      await renderTask.promise.catch(() => {})
      renderTask = null
    }
    const pdfPage = await pdf.getPage(page.number)
    if (startedGeneration !== generation) return

    const ratio = window.devicePixelRatio || 1
    const viewport = pdfPage.getViewport({ scale: atScale * ratio })
    canvas.height = viewport.height
    canvas.width = viewport.width
    renderTask = pdfPage.render({ canvasContext: canvas.getContext("2d"), viewport })
    try {
      await renderTask.promise
    } catch (e) {
      if (e?.name !== "RenderingCancelledException") throw e
    }
  }
</script>

<canvas bind:this={canvas} class="absolute h-full inset-0 w-full"></canvas>
